/**
 * Facets Accordion Animation
 * Handles smooth open/close animations for facet <details> elements
 * Required because browsers remove [open] attribute immediately on close,
 * preventing CSS transitions from running.
 */

class FacetAccordion extends HTMLElement {
  constructor() {
    super();
    this.details = null;
    this.summary = null;
    this.contentWrapper = null;
    this.isAnimating = false;
    this.initialized = false;
    
    // Bind handlers once so they can be removed later
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.initializeElements();
    
    // Set up a MutationObserver to detect when inner content is replaced (AJAX updates)
    this.setupMutationObserver();
  }

  initializeElements() {
    // Clean up old listeners if re-initializing
    if (this.summary) {
      this.summary.removeEventListener('click', this.boundHandleClick, true);
      this.summary.removeEventListener('keydown', this.boundHandleKeydown, true);
    }

    // Find the details element
    this.details = this.querySelector('details.js-filter');
    if (!this.details) return;

    this.summary = this.details.querySelector('summary');
    this.contentWrapper = this.details.querySelector('.facets__display-wrapper');

    if (!this.summary || !this.contentWrapper) return;

    // Reset animation state for fresh elements
    this.isAnimating = false;
    this.initialized = false;

    // If already open (e.g., first filter in vertical layout), allow overflow for tooltips
    if (this.details.open) {
      this.details.classList.add('animation-complete');
    }

    // Prevent default toggle and handle manually
    // Use capture phase to ensure this fires before MenuDrawer's listener
    this.summary.addEventListener('click', this.boundHandleClick, true);
    this.summary.addEventListener('keydown', this.boundHandleKeydown, true);
  }

  setupMutationObserver() {
    // Watch for child changes (AJAX content replacement)
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if our tracked elements are still in the DOM
      // When innerHTML is replaced, the elements we reference become detached
      const summaryStillConnected = this.summary && this.summary.isConnected;
      const wrapperStillConnected = this.contentWrapper && this.contentWrapper.isConnected;
      
      if (!summaryStillConnected || !wrapperStillConnected) {
        // Elements were replaced, re-initialize
        this.initializeElements();
      }
    });
    
    this.mutationObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.summary) {
      this.summary.removeEventListener('click', this.boundHandleClick, true);
      this.summary.removeEventListener('keydown', this.boundHandleKeydown, true);
    }
    
    // Clean up mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  handleClick(e) {
    e.preventDefault();
    e.stopImmediatePropagation(); // Prevent MenuDrawer's listener on this same element from firing
    this.toggle();
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopImmediatePropagation(); // Prevent MenuDrawer's listener from firing
      this.toggle();
    }
  }

  toggle() {
    if (this.isAnimating) return;

    if (this.details.open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isAnimating = true;

    // For first animation after element becomes visible, we need to ensure
    // the browser has computed the initial (closed) state before transitioning.
    // Using double-RAF ensures the initial state is painted.
    if (!this.initialized) {
      this.initialized = true;
      // Force style computation
      getComputedStyle(this.contentWrapper).gridTemplateRows;
      // Use RAF to defer the actual open to next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.performOpen();
        });
      });
    } else {
      this.performOpen();
    }
  }

  performOpen() {
    // Set open attribute to show content
    this.details.open = true;

    // Force reflow to ensure transition runs
    void this.contentWrapper.offsetHeight;

    // Listen for transition end
    const onTransitionEnd = (e) => {
      if (e.target === this.contentWrapper) {
        this.contentWrapper.removeEventListener('transitionend', onTransitionEnd);
        this.isAnimating = false;
        // Add class to allow overflow for tooltips after animation completes
        this.details.classList.add('animation-complete');
      }
    };

    this.contentWrapper.addEventListener('transitionend', onTransitionEnd);

    // Fallback timeout in case transition doesn't fire
    setTimeout(() => {
      this.isAnimating = false;
      if (this.details.open && !this.details.classList.contains('closing')) {
        this.details.classList.add('animation-complete');
      }
    }, 400);
  }

  close() {
    this.isAnimating = true;

    // Remove overflow class immediately to enable clipping during close animation
    this.details.classList.remove('animation-complete');

    // Add closing class to trigger reverse animation
    this.details.classList.add('closing');

    const onTransitionEnd = (e) => {
      if (e.target === this.contentWrapper) {
        this.contentWrapper.removeEventListener('transitionend', onTransitionEnd);
        this.details.open = false;
        this.details.classList.remove('closing');
        this.isAnimating = false;
      }
    };

    this.contentWrapper.addEventListener('transitionend', onTransitionEnd);

    // Fallback timeout in case transition doesn't fire
    setTimeout(() => {
      if (this.details.classList.contains('closing')) {
        this.details.open = false;
        this.details.classList.remove('closing');
        this.isAnimating = false;
      }
    }, 400);
  }
}

// Define the custom element
if (!customElements.get('facet-accordion')) {
  customElements.define('facet-accordion', FacetAccordion);
}

